import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  type PushNotificationSchema,
} from '@capacitor/push-notifications';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  CalendarHeart,
  ClipboardList,
  FileText,
  MessageCircle,
  MessageSquareDot,
  UserPlus,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { routeFor } from '@/hooks/usePushDeepLink';
import { BRAND_NAME } from '@/lib/companion-config';

const AUTO_DISMISS_MS = 5000;

// Per-category icon for the leading slot. Mirrors the backend
// `pushPayloads.js` categories so the visual cue matches the payload type.
// Unknown types fall back to the generic bell.
function iconFor(type: string | undefined): LucideIcon {
  switch (type) {
    case 'handoff_nudge':
      return Bell;
    case 'trfer_claimed':
      return UserPlus;
    case 'trfer_message':
      return MessageCircle;
    case 'handback':
      return MessageSquareDot;
    case 'task_due':
      return ClipboardList;
    // #2353: the Grants nav row's glyph, as the bell row wears it.
    case 'grant_decision':
    case 'claim_update':
      return FileText;
    case 'calendar_reminder':
      return CalendarHeart;
    default:
      return Bell;
  }
}

/**
 * Foreground push banner (M3 Cell 3).
 *
 * When the app is open and a push arrives, Android + iOS deliver it to the JS
 * layer via `pushNotificationReceived` but do NOT show a system notification.
 * Without this component the data would land silently — a parent actively
 * using the app would never see "a TRFer just claimed your conversation"
 * until they navigated. This component slides a small card in from the top
 * with the notification title + body, auto-dismisses after 5s, and on tap
 * routes via the shared `routeFor()` so tap behavior is identical to a
 * system-tray tap on a backgrounded notification.
 *
 * Native-only by construction: on web the platform check returns false and we
 * never register a listener (the component just renders null forever). The
 * back-to-back-push case is intentionally simple — a newer arrival replaces
 * the current one and resets the timer. A queue would be over-engineered
 * given push cadence in this app.
 *
 * Swipe-to-dismiss is a deliberate future polish; v1 ships with the X button
 * + auto-dismiss, which covers the dismissal cases that matter.
 */
export function PushBanner() {
  const navigate = useNavigate();
  const { t } = useTranslation('feedback');
  const [current, setCurrent] = useState<PushNotificationSchema | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribe to push-received events on native. The listener captures the
  // notification into local state, which drives the render below. We deliberately
  // do NOT navigate from here — that's a tap event, handled by usePushDeepLink.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let listener: { remove: () => void } | null = null;

    (async () => {
      listener = await PushNotifications.addListener(
        'pushNotificationReceived',
        (n) => setCurrent(n)
      );
    })();

    return () => {
      listener?.remove();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // (Re-)arm auto-dismiss whenever a new notification arrives. The cleanup
  // clears the timer on unmount / next change so we don't stack timers.
  useEffect(() => {
    if (!current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCurrent(null), AUTO_DISMISS_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [current]);

  if (!current) return null;

  const data = (current.data ?? {}) as Record<string, string>;
  const Icon = iconFor(data.type);
  // Platform chrome, not a family persona: staff receive pushes here too, so
  // useCompanionName() would render "TRF Williams" for them (#639).
  const title = current.title || BRAND_NAME;
  const body = current.body || '';

  const handleTap = () => {
    setCurrent(null);
    navigate(routeFor(data));
  };

  const handleDismiss = () => setCurrent(null);

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="push-banner"
      className={cn(
        'pointer-events-auto fixed inset-x-3 top-3 z-[200]',
        'rounded-2xl border border-border bg-card text-card-foreground shadow-xl',
        'animate-in slide-in-from-top-full duration-300',
        'sm:left-auto sm:right-3 sm:w-[400px]'
      )}
      style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}
    >
      <div className="flex items-start gap-3 p-3">
        <button
          type="button"
          onClick={handleTap}
          aria-label={body ? t('push.openWithBody', { title, body }) : t('push.open', { title })}
          className={cn(
            '-m-1 flex flex-1 items-start gap-3 rounded-lg p-1 text-left',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-body font-semibold leading-5">
              {title}
            </span>
            {body && (
              <span className="line-clamp-2 block text-body leading-5 text-stevens-ink-3">
                {body}
              </span>
            )}
          </span>
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('push.dismiss')}
          className={cn(
            'shrink-0 rounded-md p-1 text-stevens-ink-3',
            'hover:bg-muted hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
